import {
  Children,
  FC,
  useEffect,
  useRef,
  useState,
  isValidElement,
  PropsWithChildren,
  ReactElement,
  ReactNode,
} from 'react'
import {
  cn,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  Button,
  SeparatorProps,
} from '@wellesley/ui'
import { EllipsisIcon } from 'lucide-react'

const isSeparator = (node: ReactNode): boolean => {
  if (!isValidElement(node)) return false
  return !!(node.props as SeparatorProps)?.orientation
}

type OverflowRowProps = PropsWithChildren<{
  trigger?: ReactElement
  className?: string
}>

const OverflowRow: FC<OverflowRowProps> = ({
  trigger,
  children,
  className,
}) => {
  const childrenArray = Children.toArray(children)
  const totalItems = childrenArray.length

  const containerRef = useRef<HTMLDivElement>(null)
  const hiddenRowRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLDivElement>(null)

  const [visibleCount, setVisibleCount] = useState(0)

  const calculateVisibleItems = () => {
    if (!containerRef.current || !hiddenRowRef.current) return

    const containerWidth = containerRef.current.getBoundingClientRect().width
    const items = hiddenRowRef.current.children

    if (items.length === 0) return

    const triggerWidth = triggerRef.current
      ? triggerRef.current?.getBoundingClientRect().width
      : 0

    let currentWidth = 0
    let maxVisible = 0

    // Measure first gap and reuse
    let gap = 0
    if (items.length >= 2) {
      const firstRect = items[0].getBoundingClientRect()
      const secondRect = items[1].getBoundingClientRect()
      gap = secondRect.left - firstRect.right
    }

    for (let i = 0; i < items.length; i++) {
      const itemWidth = items[i].getBoundingClientRect().width

      // Add item width + gap (except for first item)
      let addedWidth = itemWidth
      if (i > 0) {
        addedWidth += gap
      }

      const newWidth = currentWidth + addedWidth

      // If not last item, check if we need space for dropdown button
      let totalNeeded = newWidth
      if (i < items.length - 1) {
        totalNeeded += gap + triggerWidth
      }

      if (totalNeeded <= containerWidth) {
        maxVisible = i + 1
        currentWidth = newWidth
      } else {
        break
      }
    }

    setVisibleCount(maxVisible)
  }

  // Initial calculation
  useEffect(() => {
    const timer = setTimeout(calculateVisibleItems, 0)
    return () => clearTimeout(timer)
  }, [childrenArray.length])

  // Resize calculation
  useEffect(() => {
    const observer = new ResizeObserver(() => {
      calculateVisibleItems()
    })

    if (containerRef.current) {
      observer.observe(containerRef.current)
    }

    return () => observer.disconnect()
  }, [])

  const showDropdown = totalItems > 0 && visibleCount < totalItems

  const triggerElement = trigger || (
    <Button variant="tertiary" size="icon_s">
      <EllipsisIcon className="text-gray-500" size={20} />
    </Button>
  )

  const hideClassNames =
    'pointer-events-none invisible absolute left-[-9999px] top-[-9999px]'

  return (
    <div className="relative flex w-full">
      <div
        ref={containerRef}
        data-testid="container"
        className={cn('flex w-0 flex-1 flex-nowrap overflow-hidden', className)}
      >
        {childrenArray.slice(0, visibleCount).map((child, index) => (
          <div key={index} className="flex-shrink-0">
            {child}
          </div>
        ))}

        {showDropdown && (
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger data-testid="overflow-trigger" asChild>
              {triggerElement}
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className={cn('min-w-[20px]')}
              onCloseAutoFocus={(e) => e.preventDefault()}
            >
              {childrenArray.slice(visibleCount).map((child, index) => {
                if (isSeparator(child)) return null

                return (
                  <DropdownMenuItem
                    key={index}
                    className="overflow-menu-item"
                    asChild
                  >
                    <>{child}</>
                  </DropdownMenuItem>
                )
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {/* Hidden measurement row */}
      <div
        ref={hiddenRowRef}
        className={cn(hideClassNames, 'flex flex-nowrap', className)}
        aria-hidden="true"
      >
        {childrenArray.map((child, index) => (
          <div key={index} className="flex-shrink-0">
            {child}
          </div>
        ))}
      </div>

      {/* Hidden dropdown trigger button */}
      <div
        ref={triggerRef}
        data-testid="hidden-trigger"
        className={hideClassNames}
        aria-hidden="true"
      >
        {triggerElement}
      </div>
    </div>
  )
}

export default OverflowRow
